import { React, useState, useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import ConfirmDialog from "./ConfirmDialog";
import Notification from "./Notification";
import AddStudent from "../forms/AddStudent";
import AddPopup from "../popups/AddPopup";
import RDataGrid from "./reusables/RDataGrid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { LightTooltip } from "../tooltips/ToolTip";
import PageLoader from "./reusables/PageLoader";

const Students = () => {
  const [isFetchingStudents, setIsFetchingStudents] = useState(true);
  const [students, setStudents] = useState([]);
  const STUDENTS_URL = "api/v1/students";
  const DELETE_URL = "api/v1/students/";
  const axiosPrivate = useAxiosPrivate();
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshStudents, setSetRefreshStudents] = useState(null);

  // Edit student
  const [editStudent, setEditStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(DELETE_URL + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshStudents(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Student",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingStudents) {
      setIsFetchingStudents(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getStudents = async () => {
      try {
        const response = await axiosPrivate.get(STUDENTS_URL, {
          signal: controller.signal,
        });
        isMounted && setStudents(response.data.data);
        setIsFetchingStudents(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingStudents(false);
      }
    };

    getStudents();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshStudents]);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 60,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "name",
        headerName: "Name",
        width: 150,
        align: "left",
        headerAlign: "left",
        renderCell: function (params) {
          const student = params.row;
          const name = `${student.firstName} ${student.lastName}`;
          return name;
        },
      },
      {
        field: "regNo",
        headerName: "Reg No",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "guardian_name",
        headerName: "Guardian Name",
        width: 200,
        align: "left",
        headerAlign: "left",
        renderCell: function (params) {
          const guardian = params.row.guardian;
          const name = `${guardian.firstName} ${guardian.lastName}`;
          return name;
        },
      },
      {
        field: "guardian_phone",
        headerName: "Guardian Phone",
        width: 200,
        align: "left",
        headerAlign: "left",
        renderCell: function (params) {
          const guardian = params.row.guardian;
          return guardian.phone;
        },
      },
      {
        field: "guardian_email",
        headerName: "Guardian Email",
        width: 230,
        align: "left",
        headerAlign: "left",
        renderCell: function (params) {
          const guardian = params.row.guardian;
          return guardian.email;
        },
      },
      {
        field: "route",
        headerName: "Route",
        width: 230,
        align: "left",
        headerAlign: "left",
        renderCell: function (params) {
          const route = params.row.station.route.name;
          return route;
        },
      },
      {
        field: "station",
        headerName: "Station",
        width: 160,
        align: "center",
        headerAlign: "center",
        renderCell: function (params) {
          const station = params.row.station.name;
          return station;
        },
      },
      {
        field: "pick_up_trip",
        headerName: "Pick-Up Trip",
        width: 160,
        align: "center",
        headerAlign: "center",
        renderCell: function (params) {
          const pick_up_trip = params.row.pickupTrip.name;
          return pick_up_trip;
        },
      },
      {
        field: "pickupTime",
        headerName: "Pick-Up Time",
        width: 160,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "return_trip",
        headerName: "Return Trip",
        width: 180,
        align: "center",
        headerAlign: "center",
        renderCell: function (params) {
          const return_trip = params.row.returnTrip.name;
          return return_trip;
        },
      },
      {
        field: "returnTime",
        headerName: "Drop-Off Time",
        width: 160,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit student">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedStudent = JSON.stringify(currentRow, null, 4);
                  setEditStudent(true);
                  setSelectedStudent(selectedStudent);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete student">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this student?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful day-time add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshStudents(new Date());
  };

  return isFetchingStudents ? (
    <PageLoader title="Loading students..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Box height={40} />

      {students?.length ? (
        <Box
          sx={{
            height: 560,
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ textAlign: "left", mt: 3, mb: 3 }}
            >
              Manage Students
            </Typography>
            <Button
              size="lg"
              variant="solid"
              color="primary"
              onClick={function () {
                setEditStudent(false);
                setSelectedStudent(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Add Student
            </Button>
          </div>
          <Box sx={{ width: 1218, margin: "0 auto 16px", height: 560 }}>
            <RDataGrid columns={columns} rows={students} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editStudent ? "Edit Student" : "Add Student"}
          >
            <AddStudent
              closeDialog={closeDialog}
              editStudent={editStudent}
              selectedStudent={selectedStudent}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No students to display
          </Typography>
          <Button
            size="lg"
            variant="solid"
            color="primary"
            onClick={function () {
              setEditStudent(false);
              setSelectedStudent(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Add Student
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editStudent ? "Edit Student" : "Add Student"}
          >
            <AddStudent
              closeDialog={closeDialog}
              editStudent={editStudent}
              selectedStudent={selectedStudent}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Students;
